import { useCallback, useMemo, useState } from 'react';
import Map from 'react-map-gl/maplibre';
import DeckGL from '@deck.gl/react';
import { ScatterplotLayer } from '@deck.gl/layers';
import { HexagonLayer } from '@deck.gl/aggregation-layers';
import 'maplibre-gl/dist/maplibre-gl.css';

export interface ArtistCentralityData {
  profile: {
    id: string;
    userId: string;
    displayName: string | null;
    slug: string | null;
    city: string | null;
    region: string | null;
    country: string | null;
    countryCode: string | null;
    avatarUrl: string | null;
  };
  latitude: number;
  longitude: number;
  neighborCount: number;
  nearestNeighborKm: number | null;
}

interface CentralityMapProps {
  data: ArtistCentralityData[];
  viewMode: 'scatter' | 'hexbin';
  onArtistClick?: (artist: ArtistCentralityData) => void;
  selectedArtist?: ArtistCentralityData | null;
}

const BASEMAP_STYLE = 'https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json';

const INITIAL_VIEW_STATE = {
  latitude: 39.8283,
  longitude: -98.5795,
  zoom: 3.5,
  pitch: 0,
  bearing: 0,
};

function getCentralityColor(neighborCount: number): [number, number, number, number] {
  if (neighborCount >= 10) return [255, 69, 58, 200];
  if (neighborCount >= 3) return [255, 159, 10, 200];
  if (neighborCount >= 1) return [100, 210, 255, 200];
  return [120, 120, 120, 160];
}

function CentralityMap({ data, viewMode, onArtistClick, selectedArtist }: CentralityMapProps) {
  const [viewState, setViewState] = useState(INITIAL_VIEW_STATE);
  const [hovered, setHovered] = useState<ArtistCentralityData | null>(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });

  const handleClick = useCallback(
    (info: { object?: ArtistCentralityData }) => {
      if (!info.object || !onArtistClick) return;
      onArtistClick(info.object);
      setViewState((prev) => ({
        ...prev,
        latitude: info.object!.latitude,
        longitude: info.object!.longitude,
        zoom: 8,
      }));
    },
    [onArtistClick]
  );

  const layers = useMemo(() => {
    if (viewMode === 'hexbin') {
      return [
        new HexagonLayer<ArtistCentralityData>({
          id: 'hexbin',
          data,
          getPosition: (d) => [d.longitude, d.latitude],
          radius: 50000,
          elevationScale: 200,
          extruded: true,
          colorRange: [
            [1, 152, 189],
            [73, 227, 206],
            [216, 254, 181],
            [254, 237, 177],
            [254, 173, 84],
            [209, 55, 78],
          ],
          pickable: true,
        }),
      ];
    }

    return [
      new ScatterplotLayer<ArtistCentralityData>({
        id: 'scatter',
        data,
        getPosition: (d) => [d.longitude, d.latitude],
        getFillColor: (d) => getCentralityColor(d.neighborCount),
        getRadius: (d) => (selectedArtist?.profile.id === d.profile.id ? 6 : 4),
        radiusUnits: 'pixels',
        pickable: true,
        onClick: handleClick as never,
        onHover: (info) => {
          setHovered(info.object ?? null);
          setTooltipPos({ x: info.x ?? 0, y: info.y ?? 0 });
        },
        updateTriggers: {
          getRadius: [selectedArtist?.profile.id],
        },
      }),
    ];
  }, [data, viewMode, selectedArtist, handleClick]);

  return (
    <div className="relative h-full w-full">
      <DeckGL
        viewState={viewState}
        onViewStateChange={({ viewState: vs }) => setViewState(vs as typeof viewState)}
        controller
        layers={layers}
      >
        <Map mapStyle={BASEMAP_STYLE} />
      </DeckGL>

      {hovered && viewMode === 'scatter' && (
        <div
          className="pointer-events-none absolute z-10 max-w-xs rounded-lg bg-zinc-900 px-3 py-2 text-xs text-white shadow-lg"
          style={{ left: tooltipPos.x + 12, top: tooltipPos.y - 12 }}
        >
          <p className="font-medium">{hovered.profile.displayName ?? 'Unknown'}</p>
          <p className="text-zinc-400">
            {[hovered.profile.city, hovered.profile.region].filter(Boolean).join(', ') || 'No location'}
          </p>
          <p className="text-zinc-400">
            {hovered.neighborCount} neighbor{hovered.neighborCount !== 1 ? 's' : ''}
          </p>
        </div>
      )}

      <div className="absolute bottom-4 left-4 flex items-center gap-3 rounded-lg bg-zinc-900/80 px-3 py-2 text-[11px] text-zinc-300 backdrop-blur">
        <span className="flex items-center gap-1">
          <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: 'rgb(255,69,58)' }} /> 10+
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: 'rgb(255,159,10)' }} /> 3-9
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: 'rgb(100,210,255)' }} /> 1-2
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: 'rgb(120,120,120)' }} /> 0
        </span>
      </div>
    </div>
  );
}

export default CentralityMap;
